// src/components/optimiseur/QuestionsCandidat.jsx — LES QUESTIONS AU CANDIDAT,
// QUI ATTENDENT LEUR RÉPONSE.
//
// 31.10, après la relecture d'un CV optimisé par un tiers (« l'optimiseur a
// collé des mots-clés en bout de phrase ») : « une petite section simple :
// les questions à poser au candidat ; dès qu'on a la réponse, je la colle ;
// et si je laisse le candidat pour la prochaine fois, je dois retrouver les
// mêmes questions en attente de ma réponse ».
//
// Les questions sont SERVIES et gardées par le serveur (une par exigence à
// confirmer, client masqué, plus celles qu'on écrit soi-même) : l'écran les
// numérote telles qu'elles arrivent, copie le texte prêt à envoyer, et colle
// les réponses — une par une, ou d'un bloc numéroté. Leman écrit chaque
// réponse dans la bonne mission ; « pas fait » devient un écart à aborder en
// entretien. Le CV se régénère une fois, quand on a fini.
//
//   GET   /besoins/:bid/cv-questions/:cid                → { questions, n_en_attente, texte_a_copier }
//   POST  /besoins/:bid/cv-questions/:cid                { question, critere? }
//   POST  /besoins/:bid/cv-questions/:cid/coller         { texte }
//   POST  /besoins/:bid/cv-questions/:cid/:qid/repondre  { reponse, pas_fait }
//   PATCH /besoins/:bid/cv-questions/:cid/:qid           { statut: 'ecartee' | 'en_attente' }
import React, { useCallback, useEffect, useState } from 'react';
import { appel, dateHeure } from './api';

const BI = ({ icon }) => <i className={`bi bi-${icon}`} aria-hidden="true" />;
const Spin = () => <i className="bi bi-arrow-repeat tq-spin" aria-hidden="true" />;
const MAX_QUESTION = 300;
const MAX_REPONSE = 1000;

export default function QuestionsCandidat({ besoinId, candidateId, onReponses, onRegenerer, peutGenerer = true, regenEnCours = false, showNotification }) {
  const [etat, setEtat] = useState({ etat: 'loading', questions: [], texte_a_copier: '', n_en_attente: 0 });
  const [brouillons, setBrouillons] = useState({});
  const [busy, setBusy] = useState(null);
  const [collage, setCollage] = useState(null);     // null : fermé ; texte sinon
  const [nouvelle, setNouvelle] = useState('');
  const [voirRepondues, setVoirRepondues] = useState(false);
  const [aRegenerer, setARegenerer] = useState(false);

  const charger = useCallback(async () => {
    try {
      const { r, j, absente } = await appel(`/besoins/${besoinId}/cv-questions/${candidateId}`);
      if (absente) { setEtat({ etat: 'route', questions: [], texte_a_copier: '', n_en_attente: 0 }); return; }
      if (!r.ok || j.success === false) throw new Error(j.error || `Les questions n’ont pas pu être lues (${r.status}).`);
      setEtat({ etat: 'ready', questions: Array.isArray(j.questions) ? j.questions : [], texte_a_copier: j.texte_a_copier || '', n_en_attente: j.n_en_attente || 0 });
    } catch (e) { setEtat(x => ({ ...x, etat: 'error', erreur: e.message })); }
  }, [besoinId, candidateId]);
  useEffect(() => { charger(); }, [charger]);

  const envoyer = async (cle, chemin, corps, method = 'POST') => {
    setBusy(cle);
    try {
      const { r, j, absente } = await appel(chemin, { method, corps });
      if (absente) throw new Error('Les questions au candidat arrivent côté serveur.');
      if (!r.ok || j.success === false) throw new Error(j.error || `Ça n’a pas abouti (${r.status}).`);
      return j;
    } catch (e) { showNotification?.(e.message, 'error'); return null; }
    finally { setBusy(null); }
  };
  const apres = async (j) => {
    if (!j) return;
    if (j.message) showNotification?.(j.message, j.a_regenerer || /enregistr|écrit|gardée/i.test(j.message) ? 'success' : 'info');
    if (j.a_regenerer) setARegenerer(true);
    await charger();
    onReponses?.(j);
  };

  const repondre = async (q, pasFait) => {
    const texte = (brouillons[q.id] || '').trim();
    if (!pasFait && !texte) { showNotification?.('Collez la réponse du candidat, ou cliquez « Pas fait ».', 'error'); return; }
    const j = await envoyer(q.id, `/besoins/${besoinId}/cv-questions/${candidateId}/${q.id}/repondre`, pasFait ? { pas_fait: true, ...(texte ? { reponse: texte } : {}) } : { reponse: texte });
    if (j) setBrouillons(b => { const n = { ...b }; delete n[q.id]; return n; });
    await apres(j);
  };
  const coller = async () => {
    const texte = (collage || '').trim();
    if (!texte) { showNotification?.('Collez les réponses du candidat.', 'error'); return; }
    const j = await envoyer('coller', `/besoins/${besoinId}/cv-questions/${candidateId}/coller`, { texte });
    if (j) {
      setCollage(null);
      if (Array.isArray(j.erreurs) && j.erreurs.length) showNotification?.(j.erreurs.join(' · '), 'error');
    }
    await apres(j);
  };
  const ajouter = async () => {
    const q = nouvelle.trim();
    if (!q) return;
    const j = await envoyer('ajouter', `/besoins/${besoinId}/cv-questions/${candidateId}`, { question: q });
    if (j) setNouvelle('');
    await apres(j);
  };
  const statut = async (q, s) => apres(await envoyer(q.id, `/besoins/${besoinId}/cv-questions/${candidateId}/${q.id}`, { statut: s }, 'PATCH'));
  const copier = async () => {
    try { await navigator.clipboard.writeText(etat.texte_a_copier); showNotification?.('Questions copiées : collez-les dans votre message au candidat.', 'success'); }
    catch (e) { showNotification?.(e.message, 'error'); }
  };

  const attente = etat.questions.filter(q => q.statut === 'en_attente');
  const repondues = etat.questions.filter(q => q.statut === 'repondue');
  const ecartees = etat.questions.filter(q => q.statut === 'ecartee');

  return (
    <section className="opt-section opt-questions">
      <div className="opt-section__tete">
        <h3 className="opt-section__titre">Questions au candidat</h3>
        {etat.etat === 'ready' && attente.length > 0 && <span className="opt-section__meta">{attente.length} en attente</span>}
      </div>
      <p className="opt-note">Une idée par question. Copiez-les pour les envoyer ; quand les réponses arrivent, collez-les : Leman écrit chaque réponse dans la bonne mission, un « pas fait » devient un écart à aborder en entretien. Elles vous attendent ici d’une visite à l’autre.</p>

      {etat.etat === 'loading' && <p className="opt-muet"><Spin /> Lecture des questions…</p>}
      {etat.etat === 'route' && <p className="opt-muet">Les questions au candidat arrivent côté serveur.</p>}
      {etat.etat === 'error' && <p className="opt-erreur">{etat.erreur}</p>}

      {etat.etat === 'ready' && (
        <>
          {attente.length === 0 && (
            <p className="opt-muet">{repondues.length ? 'Toutes les questions ont leur réponse.' : 'Rien à demander pour l’instant : les points à confirmer de l’analyse arrivent ici.'}</p>
          )}
          {attente.map(q => {
            const texte = brouillons[q.id] || '';
            const occupe = busy === q.id;
            return (
              <div key={q.id} className="opt-oui-non__ligne">
                <div className="opt-oui-non__tete">
                  <span className="opt-reco__n">{q.numero}</span>
                  <span className="opt-oui-non__question">{q.question}</span>
                  {q.origine === 'recruteur' && <span className="opt-section__meta">votre question</span>}
                </div>
                <textarea className="erp-field" rows={2} maxLength={MAX_REPONSE} value={texte} disabled={!!busy}
                  placeholder="Collez sa réponse, avec ses mots…" aria-label={`Réponse à la question ${q.numero}`}
                  onChange={e => setBrouillons(b => ({ ...b, [q.id]: e.target.value }))} />
                <div className="opt-oui-non__actions">
                  <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" onClick={() => repondre(q, false)} disabled={!!busy || !texte.trim()}>
                    {occupe ? <Spin /> : <BI icon="check2" />} Enregistrer la réponse
                  </button>
                  <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => repondre(q, true)} disabled={!!busy}>
                    <BI icon="x-lg" /> Pas fait
                  </button>
                  <button type="button" className="tuto-bulle__lien" onClick={() => statut(q, 'ecartee')} disabled={!!busy}>Ne pas poser</button>
                </div>
              </div>
            );
          })}

          {attente.length > 0 && (
            <div className="opt-oui-non__pied">
              <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={copier} disabled={!etat.texte_a_copier}>
                <BI icon="clipboard" /> Copier les questions
              </button>
              <button type="button" className="tuto-bulle__lien" aria-expanded={collage != null} onClick={() => setCollage(c => (c == null ? '' : null))}>
                <BI icon="clipboard-plus" /> {collage == null ? 'Coller les réponses d’un bloc' : 'Fermer'}
              </button>
            </div>
          )}
          {collage != null && (
            <div className="opt-champ">
              <label className="opt-champ__lib" htmlFor={`opt-collage-${candidateId}`}>Les réponses du candidat, numérotées comme les questions (1. … 2. …)</label>
              <textarea id={`opt-collage-${candidateId}`} className="erp-field" rows={5} value={collage} disabled={!!busy}
                placeholder={'1. Oui, chez …\n2. Non, pas fait.'} onChange={e => setCollage(e.target.value)} />
              <div className="opt-oui-non__actions">
                <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" onClick={coller} disabled={!!busy || !collage.trim()}>
                  {busy === 'coller' ? <Spin /> : <BI icon="check2-all" />} Enregistrer les réponses
                </button>
              </div>
            </div>
          )}

          <div className="opt-champ opt-champ--ligne">
            <input className="erp-field" type="text" value={nouvelle} maxLength={MAX_QUESTION} disabled={!!busy}
              placeholder="Une question à vous (un trou dans le parcours, une disponibilité…)" aria-label="Ajouter une question"
              onChange={e => setNouvelle(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); ajouter(); } }} />
            <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={ajouter} disabled={!!busy || !nouvelle.trim()}>
              {busy === 'ajouter' ? <Spin /> : <BI icon="plus-lg" />} Ajouter
            </button>
          </div>

          {aRegenerer && (
            <div className="opt-regen">
              <p className="opt-note">Le CV ne porte pas encore les réponses écrites.</p>
              {peutGenerer && onRegenerer && (
                <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" onClick={() => { setARegenerer(false); onRegenerer(); }} disabled={regenEnCours || !!busy}>
                  {regenEnCours ? <Spin /> : <BI icon="file-earmark-text" />} Régénérer le CV
                </button>
              )}
            </div>
          )}

          {(repondues.length > 0 || ecartees.length > 0) && (
            <div className="opt-details">
              <button type="button" className="tuto-bulle__lien opt-plus" aria-expanded={voirRepondues} onClick={() => setVoirRepondues(v => !v)}>
                <BI icon={voirRepondues ? 'chevron-up' : 'chevron-down'} /> {voirRepondues ? 'Masquer les réponses' : `Voir les réponses (${repondues.length})${ecartees.length ? ` et les questions écartées (${ecartees.length})` : ''}`}
              </button>
              {voirRepondues && [...repondues, ...ecartees].map(q => (
                <div key={q.id} className="opt-reponse">
                  <div className="opt-reponse__corps">
                    <div className="opt-reponse__critere">{q.question}</div>
                    {q.statut === 'ecartee'
                      ? <div className="opt-note">Non posée.</div>
                      : (
                        <>
                          {(q.reponse || q.pas_fait) && <div className="opt-reponse__texte">{q.reponse || 'Pas fait.'}</div>}
                          <div className="opt-note">
                            {[q.resultat_libelle, q.detail?.mission_libelle, q.detail?.raison, q.detail?.par ? `par un « ${q.detail.par} »` : null, q.repondu_le ? dateHeure(q.repondu_le) : null].filter(Boolean).join(' · ')}
                          </div>
                          {q.detail?.texte && <div className="opt-reponse__texte"><BI icon="arrow-return-right" /> {q.detail.texte}</div>}
                        </>
                      )}
                    <button type="button" className="tuto-bulle__lien" onClick={() => statut(q, 'en_attente')} disabled={!!busy}>Reposer la question</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
